import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Smartphone, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Register from './Register';
import { useIsMobile } from '@/hooks/use-mobile';
import PWAInstallModal from '@/components/Auth/PWAInstallModal';

const AuthPage = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showPwaModal, setShowPwaModal] = useState(false);
  const [pwaPlatform, setPwaPlatform] = useState<'ios' | 'android'>('ios');
  const { user, signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Verificar si hay error en la URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error) {
      console.error('Auth error:', error, errorDescription);
      // Limpiar la URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleOpenInstall = (platform: 'ios' | 'android') => {
    setPwaPlatform(platform);
    setShowPwaModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const { error } = await signIn(email.trim());
      if (!error) {
        setEmailSent(true);
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-emerald-500" />
            </div>
            <CardTitle className="text-2xl font-bold">{t('auth.link_sent_title')}</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              {t('auth.link_sent_p1')} <strong>{email}</strong>
            </p>
            <p className="text-sm text-muted-foreground">
              {t('auth.link_sent_p2')}
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                setEmailSent(false);
                setEmail('');
              }}
              className="w-full"
            >
              {t('auth.send_another')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-8">
      
      <Card className="w-full max-w-md border-border/70 shadow-xl bg-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img
              src="/logo.png"
              alt="BASS Line"
              className="h-16 w-auto"
            />
          </div>
          {view === 'register' && (
            <p className="text-sm text-muted-foreground">{t('auth.subtitle_register')}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t('auth.continue_with')}</span>
            </div>
          </div>

          <Button 
            type="button"
            variant="outline"
            className="w-full border-input text-foreground hover:bg-muted flex items-center justify-center gap-2"
            onClick={async () => {
              await signInWithGoogle();
              navigate('/');
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('auth.google_btn')}
          </Button>

          {view === 'login' ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email_label')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading || !email.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('auth.sending')}
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      {t('auth.send_magic_link')}
                    </>
                  )}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('auth.no_account')}{' '}
                  <button
                    type="button"
                    onClick={() => setView('register')}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('auth.register_here')}
                  </button>
                </p>
              </div>
            </>
          ) : (
            <Register onSwitchToLogin={() => setView('login')} />
          )}

          {/* ======================================================================
              PWA MOBILE APP INSTALL TUTORIAL TRIGGER BUTTONS (LOGIN PAGE)
              ====================================================================== */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground flex items-center gap-1.5 font-medium">
                <Smartphone className="w-3.5 h-3.5 text-primary" />
                <span>Instalar App Móvil</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenInstall('ios')}
              className="w-full border-input text-foreground hover:bg-muted flex items-center justify-center gap-2 h-9 text-xs font-medium transition-all"
            >
              <svg className="w-4 h-4 fill-current text-foreground" viewBox="0 0 24 24">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.74 1.04-1.79.92-2.87-1 .04-2.14.67-2.79 1.42-.56.64-1.05 1.7-0.92 2.74 1.12.09 2.19-.55 2.79-1.29z"/>
              </svg>
              <span>iPhone (Safari)</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenInstall('android')}
              className="w-full border-input text-foreground hover:bg-muted flex items-center justify-center gap-2 h-9 text-xs font-medium transition-all"
            >
              <svg className="w-4 h-4 fill-current text-[#34A853]" viewBox="0 0 24 24">
                <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.411 13.8562 8.084 12 8.084c-1.8562 0-3.5902.327-5.1368.8657L4.8409 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-9.4396"/>
              </svg>
              <span>Android (Chrome)</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* PWA 3-Step Install Tutorial Modal */}
      <PWAInstallModal
        isOpen={showPwaModal}
        onOpenChange={setShowPwaModal}
        defaultPlatform={pwaPlatform}
      />
    </div>
  );
};

export default AuthPage;
